/**
 * utils/contactImportResolve.js
 * Contacts xlsx import, batch 3a of 5 (the RESOLUTION LAYER): sits between the pure parser
 * (`contactImportParser.js`, batch 1) and the pure commit layer (`contactImportCommit.js`,
 * batch 2). Takes the parser's structured shop rows plus the user's own decisions and produces
 * the clean, per-shop plan the commit layer's `commitContactImport` expects. No React, no UI —
 * batch 5 builds the screens that actually ask a human these questions and feed the answers back
 * in as `decisions`.
 *
 * ── WHAT THIS RESOLVES — six kinds of ambiguity, all confirmed against the real file, and the
 *    app NEVER decides any of them itself; it only applies whatever the user chose ─────────────
 *   1. DUPLICATE NAMES within one sheet (26 real groups: CABANG 22, PUSAT 4) — one shop or two,
 *      ALWAYS asked, even when every address on the duplicate rows is identical. Choosing "one
 *      shop" merges every field from every row in the group onto one contact. Choosing "two
 *      shops" keeps them separate — but this app's own uniqueness rule (`contacts.js`'s
 *      `nameCollides`) is per-list and case-insensitive, so two contacts can never share a name
 *      in the same list; if the user's chosen final names still collide after "two shops," that
 *      is reported as its own blocking problem (`duplicate-name-conflict`), never silently
 *      resolved by dropping the second row.
 *   2. EXTRA ADDRESSES (6 real cases) — a shop with more than one address (always from a
 *      continuation/blank row attaching a second address) needs a decision: genuinely a second
 *      address (keep all), or the same address typed twice (drop the duplicate index/es). Never
 *      guessed; a shop with >1 address and no decision stays unresolved.
 *   3. UNUSABLE SALES (2 real cases, both a genuine Excel error cell) — a shop whose primary
 *      Sales cell couldn't be read needs either a real name typed in, or an explicit skip. This
 *      layer only ever captures WHICH NAME belongs to the row (`salesNames` on the plan row,
 *      below) — mapping a name to a real Person id is batch 4's job, not this one.
 *   4. MISSING ADDRESS (4 real cases, all PUSAT) — a shop with zero addresses needs a decision:
 *      supply one, or explicitly leave it empty (a real, valid answer — not the same as "not yet
 *      asked").
 *   5. UNDERIVED CITY (37 real cases: CABANG 1, PUSAT 36) — a shop whose city couldn't be
 *      derived from its address needs a decision: a typed city, or explicitly blank. A shop whose
 *      city WAS derived needs no decision to reach the plan, but the user may still override it —
 *      either way, the plan row's own `_meta.citySource` states plainly whether the final value
 *      is `"derived"` (the app's own guess, never stored as though the user chose it) or
 *      `"chosen"` (a real human answer, confirmed or corrected).
 *   6. POSSIBLE SECOND SALES (1 real case: CABANG row 225, a continuation of row 224 — added
 *      after the batch-5c documentation audit found `contactImportParser.js`'s own
 *      `possible-second-sales` problem was raised but read by NOTHING downstream, silently
 *      dropping the second salesperson) — a continuation row whose own Sales disagrees with its
 *      parent's needs a decision: ADD it as a genuine second salesperson for this shop
 *      (`{action:"add"}`, folds `problem.rowSales` into `salesNames` alongside the parent's own
 *      name — exactly the same array a `duplicate-name` merge of two named rows already produces
 *      two entries in, so this is not a new kind of shape, just a new way to reach it), or KEEP
 *      only the parent's own name (`{action:"keep"}`, today's pre-existing behavior, made
 *      EXPLICIT rather than silent). **Never built for "the one real occurrence"** — a shop can
 *      carry any number of `possible-second-sales` problems (`eff.secondSalesProblems`, built by
 *      `buildEffectiveShops`'s own `withSecondSalesProblems`), each its own independent decision,
 *      keyed by the CONTINUATION row (never the parent's, since one parent could in principle
 *      have several disagreeing continuation rows, each a separate question). Whichever name gets
 *      ADDED is not itself resolved here — it lands in `salesNames` as a plain raw string, exactly
 *      like any other name, so batch 4's own cross-sheet/case-insensitive `salesNameKey` merging
 *      naturally collapses it into an EXISTING decision if the same name already appears
 *      elsewhere in the file, or gives it its own new one if it doesn't — no special-casing
 *      needed anywhere past this layer.
 *
 * ── THE SHAPE OF A DECISION (mirrors ImportWizard's own discipline — see that file's own
 *    `overrides[i]` comment) ────────────────────────────────────────────────────────────────────
 * `decisions` is a plain object with six optional sub-maps: `{ duplicateNames, extraAddresses,
 * unusableSales, missingAddress, city, secondSales }` (an absent sub-map means "nothing answered
 * yet" for that whole category — never a crash). Each sub-map is keyed by a STABLE STRING this
 * module builds and exports (`duplicateGroupKey(sheet, name)` for duplicates, `shopKey(sheet,
 * row)` for the other five) — batch 5 must build/read keys through these exports, never by
 * re-deriving the string itself, so a future change to the key format can't silently desync the
 * two sides.
 *
 * **An UNANSWERED question is distinguished from one answered with a blank/default by
 * PRESENCE, never by truthiness.** Every read in this file goes through `readDecision`, which
 * checks `key in map`, never `map[key] || fallback` — the exact bug ImportWizard's own review
 * comment warns about (a falsy-but-real answer, e.g. `{text:""}` for "leave the address empty,"
 * would otherwise be silently discarded and re-asked forever). Each decision TYPE also has its
 * own `normalize*Decision` export that destructures a raw stored value the same defensive way
 * ImportWizard's own `overrides[i]` read does — a decision value may be a bare string (the
 * common case) or an object carrying extra detail (e.g. `{kind:"separate", names:{...}}`); never
 * read it as one shape without checking `typeof` first.
 *
 * ── WHAT THIS PRODUCES ──────────────────────────────────────────────────────────────────────
 * `resolveContactImport(parsed, decisions)` → `{ plan, unresolved, excluded, readyToCommit }`.
 * - `plan`: one row per FULLY resolved shop, matching `contactImportCommit.js`'s own plan-row
 *   shape (`list, name, addresses, city, contactPerson, email, keterangan, npwp, ekspedisi`) —
 *   `commitContactImport`'s own `fieldsFromRow` reads only those exact keys, so extra ones are
 *   harmless. **`personIds` is deliberately ABSENT.** In its place, `salesNames: string[]` carries
 *   every raw sales name this shop resolved to (one shop can genuinely have more than one, e.g. a
 *   duplicate-name group merged from two rows each with their own valid Sales cell, OR a
 *   `possible-second-sales` decision — ambiguity 6, above — answered "add") — batch 4
 *   maps each name to a real Person id and is the one that actually sets `personIds` before a row
 *   ever reaches `commitContactImport`. A `_meta` object (`sheet, anchorRow, sourceRows,
 *   mergedFromRows, citySource`) carries traceability + the derived/chosen distinction for batch
 *   5's own display — never read by the commit layer, which only picks named fields.
 *   `contactPerson`/`email`/`keterangan`/`npwp` are collapsed from the parser's own per-shop
 *   ARRAYS into the single string `Contact` fields want — dedupe exact repeats (trim + collapse
 *   whitespace + case-insensitive), join everything else with ", ", nothing ever silently
 *   dropped: two genuinely different contact numbers on one shop both survive, joined into one
 *   string. `ekspedisi` stays an array (`Contact.ekspedisi` already is one) with the same
 *   exact-repeat dedupe, nothing more. Every imported address's `isDefault` is always `false` —
 *   this layer never guesses a default, same as everywhere else in this app; a human picks one
 *   later via `ContactFormModal`.
 * - `unresolved`: every still-open question blocking a shop from `plan`, one entry per pending
 *   item (a shop can carry more than one at once) — `{type, sheet, decisionKey, ...}`. Batch 5
 *   must refuse to commit while this is non-empty and use it to say exactly what's outstanding.
 * - `excluded`: a shop that reached FULL resolution but ended up with zero `salesNames` (every
 *   contributing row's Sales was skipped) — `Contact` requires >=1 person, so this shop can never
 *   become a contact. Reported explicitly (`type:"no-person"`), never silently dropped and never
 *   left to fail later, invisibly, inside `commitContactImport`.
 * - `readyToCommit`: `unresolved.length === 0` — batch 5's own gate; `excluded` shops never block
 *   this, since they're a fully-resolved, deliberate outcome, not a pending question.
 *
 * `collectDecisionsNeeded(parsed)` is a read-only convenience for batch 5: calls this module
 * with zero decisions and returns `unresolved` grouped by type, so a wizard step can enumerate
 * exactly what it needs to ask without re-deriving any grouping logic of its own.
 *
 * ── WHAT THIS DOES NOT DO ───────────────────────────────────────────────────────────────────
 * - Does not map an ADDED second Sales name to a real Person id — `salesNames` still only ever
 *   carries raw strings; batch 4 (`contactImportSalesMap.js`) is what turns any name, first or
 *   second, into a `personId`. This layer's own job for ambiguity 6 stops at deciding WHETHER the
 *   second name belongs in `salesNames` at all.
 * - Does not check a renamed "two shops" row against any name OUTSIDE its own duplicate group —
 *   only the within-group collision is checked here (the one the task's own decision shape
 *   requires). A rename that happens to collide with an unrelated shop elsewhere in the same
 *   sheet is still never silently lost: `commitContactImport`'s own `nameCollides` check catches
 *   it at commit time and reports it as a normal skip, just one step later than this layer could.
 * - Does not re-derive a shop's city after an address-dedup decision — city is always the value
 *   the PARSER derived from the shop's original, un-deduped primary address; dedup only changes
 *   which address rows are kept, never which one governs city.
 * - Never drops the shop's own primary (first-listed) address via a dedupe decision — index 0
 *   always survives regardless of what a `dropIndices` decision names. If the primary address is
 *   itself the one typed twice, the fix is to drop the LATER duplicate instead; this keeps a
 *   dedupe decision from ever leaving a shop with zero addresses by construction.
 * - Does not support partial merges of a 3+-row duplicate group (some rows merged, others not) —
 *   a group resolves as either fully one shop or fully separate rows. Real duplicate groups in
 *   this app's own data are 2-wide except one 3-wide PUSAT group; if a genuine partial-merge need
 *   ever shows up, that is a follow-up product decision, not something this layer guesses at.
 */

function normShopName(s) {
  return (s == null ? "" : String(s)).trim().replace(/\s+/g, " ").toUpperCase();
}

/** Stable decision-map key for a duplicate-name group — export so batch 5 never re-derives it. */
export function duplicateGroupKey(sheet, name) {
  return `dup:${sheet}::${normShopName(name)}`;
}

/** Stable decision-map key for a single row (extra-address / unusable-sales / missing-address /
 *  city) — export so batch 5 never re-derives it. Safe to reuse across the four different
 *  decision sub-maps: each lives in its own top-level object, so an identical key string never
 *  collides across categories. */
export function shopKey(sheet, row) {
  return `row:${sheet}:${row}`;
}

/** `key in map`, never `map[key] || fallback` — see the file header's own warning. */
function readDecision(map, key) {
  if (!map || !Object.prototype.hasOwnProperty.call(map, key)) {
    return { answered: false, value: undefined };
  }
  return { answered: true, value: map[key] };
}

export function normalizeDuplicateDecision(raw) {
  const isObj = raw != null && typeof raw === "object";
  const kind = isObj ? raw.kind : raw;
  const names = isObj && raw.names && typeof raw.names === "object" ? raw.names : {};
  return { kind, names };
}

export function normalizeAddressDecision(raw) {
  const isObj = raw != null && typeof raw === "object";
  const action = isObj ? raw.action : raw;
  const dropIndices = isObj && Array.isArray(raw.dropIndices) ? raw.dropIndices : [];
  return { action, dropIndices };
}

export function normalizeSalesDecision(raw) {
  const isObj = raw != null && typeof raw === "object";
  const action = isObj ? raw.action : raw;
  const name = isObj && typeof raw.name === "string" ? raw.name.trim() : "";
  return { action, name };
}

export function normalizeMissingAddressDecision(raw) {
  const isObj = raw != null && typeof raw === "object";
  const text = isObj ? (typeof raw.text === "string" ? raw.text : "") : typeof raw === "string" ? raw : "";
  return { text };
}

export function normalizeCityDecision(raw) {
  const isObj = raw != null && typeof raw === "object";
  const city = isObj ? (typeof raw.city === "string" ? raw.city : "") : typeof raw === "string" ? raw : "";
  return { city };
}

/** `{action: "add"|"keep"}` — "add" folds the continuation row's own Sales name into this shop's
 *  `salesNames` as a genuine second entry; "keep" leaves the shop with only its parent's own
 *  name, exactly as it already behaved before this decision existed. No default: an absent or
 *  unrecognized `action` is NOT "keep" — it's still unanswered (see `resolveEffectiveShop`'s own
 *  `possible-second-sales` handling), so the app never silently picks one on the user's behalf. */
export function normalizeSecondSalesDecision(raw) {
  const isObj = raw != null && typeof raw === "object";
  const action = isObj ? raw.action : raw;
  return { action };
}

/** Flattens an array-of-arrays into deduped (trim + collapse-whitespace + case-insensitive),
 *  order-preserving strings — the one place this module ever collapses the parser's per-shop
 *  arrays. Nothing is ever silently dropped except an EXACT repeat. */
function dedupeArray(arrays) {
  const seen = new Set();
  const out = [];
  for (const arr of arrays || []) {
    for (const raw of arr || []) {
      const t = (raw == null ? "" : String(raw)).trim();
      if (t === "") continue;
      const k = t.replace(/\s+/g, " ").toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(t);
    }
  }
  return out;
}

/** `Contact.contactPerson`/`.email`/`.keterangan`/`.npwp` are single strings; the parser hands
 *  each shop an ARRAY. Collapsed here by joining every distinct (deduped) value with ", " — a
 *  shop with two genuinely different contact numbers keeps both, joined into one string, never
 *  silently loses the second one. */
function collapseList(arrays) {
  return dedupeArray(arrays).join(", ");
}

function groupBy(arr, fn) {
  const m = new Map();
  for (const item of arr) {
    const k = fn(item);
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(item);
  }
  return m;
}

function groupShopsByName(shops) {
  const groups = new Map();
  for (const shop of shops) {
    const key = normShopName(shop.name);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(shop);
  }
  return groups;
}

/** One un-grouped shop (or a "separate"-resolved member of a former duplicate group, possibly
 *  renamed) as this module's internal, uniform "effective shop" shape. */
function buildSingleEffective(sheet, shop, overrideName) {
  return {
    sheet,
    anchorRow: shop.row,
    sourceRows: [...shop.sourceRows],
    mergedFromRows: [shop.row],
    name: overrideName || shop.name,
    addresses: [...shop.addresses],
    ekspedisi: [shop.ekspedisi],
    contactPerson: [shop.contactPerson],
    email: [shop.email],
    keterangan: [shop.keterangan],
    npwp: sheet === "PUSAT" ? [shop.npwp] : null,
    city: shop.city,
    salesRows: [{ row: shop.row, sales: shop.sales }],
  };
}

/** A "merge"-resolved duplicate-name group, combined onto one effective shop. The anchor (lowest
 *  row number — first appearance) supplies the final name and the city (see the file header's
 *  "does not re-derive" note); every other field concatenates every member's own array, in row
 *  order. */
function buildMergedEffective(sheet, sortedShops) {
  const anchor = sortedShops[0];
  return {
    sheet,
    anchorRow: anchor.row,
    sourceRows: sortedShops.flatMap((s) => s.sourceRows).sort((a, b) => a - b),
    mergedFromRows: sortedShops.map((s) => s.row),
    name: anchor.name,
    addresses: sortedShops.flatMap((s) => s.addresses),
    ekspedisi: sortedShops.map((s) => s.ekspedisi),
    contactPerson: sortedShops.map((s) => s.contactPerson),
    email: sortedShops.map((s) => s.email),
    keterangan: sortedShops.map((s) => s.keterangan),
    npwp: sheet === "PUSAT" ? sortedShops.map((s) => s.npwp) : null,
    city: anchor.city,
    salesRows: sortedShops.map((s) => ({ row: s.row, sales: s.sales })),
  };
}

/** Resolves one effective shop against the four per-row decision categories (duplicate-name is
 *  already settled by the time this runs) and pushes the result into exactly one of
 *  `ctx.plan`/`ctx.unresolved`/`ctx.excluded`. */
function resolveEffectiveShop(sheet, eff, ctx) {
  const { addressDecisions, salesDecisions, missingAddressDecisions, cityDecisions, secondSalesDecisions, unresolved, excluded, plan } = ctx;
  const rowKey = shopKey(sheet, eff.anchorRow);
  let blocked = false;

  // --- addresses: extra-address dedup OR missing-address (mutually exclusive by count) ---
  let finalAddresses = eff.addresses;
  if (eff.addresses.length > 1) {
    const { answered, value } = readDecision(addressDecisions, rowKey);
    const pushUnresolved = () =>
      unresolved.push({
        type: "extra-address",
        sheet,
        decisionKey: rowKey,
        row: eff.anchorRow,
        name: eff.name,
        addresses: eff.addresses,
        message: `Toko "${eff.name}" (baris ${eff.anchorRow}) punya ${eff.addresses.length} alamat — perlu diputuskan mana yang benar-benar berbeda dan mana yang duplikat.`,
      });
    if (!answered) {
      blocked = true;
      pushUnresolved();
    } else {
      const { action, dropIndices } = normalizeAddressDecision(value);
      if (action === "dedupe") {
        const drop = new Set(dropIndices.filter((i) => Number.isInteger(i) && i > 0 && i < eff.addresses.length));
        finalAddresses = eff.addresses.filter((_, i) => !drop.has(i));
      } else if (action === "keepAll") {
        finalAddresses = eff.addresses;
      } else {
        blocked = true;
        pushUnresolved();
      }
    }
  } else if (eff.addresses.length === 0) {
    const { answered, value } = readDecision(missingAddressDecisions, rowKey);
    if (!answered) {
      blocked = true;
      unresolved.push({
        type: "missing-address",
        sheet,
        decisionKey: rowKey,
        row: eff.anchorRow,
        name: eff.name,
        message: `Toko "${eff.name}" (baris ${eff.anchorRow}) tidak punya alamat — isi alamatnya, atau biarkan kosong.`,
      });
    } else {
      const { text } = normalizeMissingAddressDecision(value);
      finalAddresses = text.trim() ? [text.trim()] : [];
    }
  }

  // --- city: required only when the parser couldn't derive one; always overridable ---
  let finalCity = eff.city.value || "";
  let citySource = "derived";
  const cityAnswer = readDecision(cityDecisions, rowKey);
  if (cityAnswer.answered) {
    const { city } = normalizeCityDecision(cityAnswer.value);
    finalCity = city;
    citySource = "chosen";
  } else if (!eff.city.derived) {
    blocked = true;
    unresolved.push({
      type: "city-not-derived",
      sheet,
      decisionKey: rowKey,
      row: eff.anchorRow,
      name: eff.name,
      message: `Kota untuk toko "${eff.name}" (baris ${eff.anchorRow}) tidak bisa diturunkan dari alamat — isi kotanya, atau biarkan kosong.`,
    });
  }

  // --- sales: one usable-or-resolved name per contributing row (>=1 for a merge) ---
  const salesNames = [];
  for (const sr of eff.salesRows) {
    if (sr.sales) {
      salesNames.push(sr.sales);
      continue;
    }
    const key = shopKey(sheet, sr.row);
    const { answered, value } = readDecision(salesDecisions, key);
    const pushUnresolved = () =>
      unresolved.push({
        type: "unusable-sales",
        sheet,
        decisionKey: key,
        row: sr.row,
        name: eff.name,
        message: `Toko "${eff.name}" (baris ${sr.row}) tidak punya nilai Sales yang bisa dipakai — isi nama sales yang benar, atau lewati toko ini.`,
      });
    if (!answered) {
      blocked = true;
      pushUnresolved();
      continue;
    }
    const { action, name } = normalizeSalesDecision(value);
    if (action === "provide" && name) {
      salesNames.push(name);
    } else if (action === "skip") {
      // Deliberately contributes nothing — see the "excluded" / no-person handling below.
    } else {
      blocked = true;
      pushUnresolved();
    }
  }

  // --- possible-second-sales: an attached continuation row's own Sales disagreed with its
  // parent's (contactImportParser.js's own `possible-second-sales` problem). Genuinely never
  // built for "the one real occurrence" — `eff.secondSalesProblems` can hold any number, each
  // resolved independently, keyed by the CONTINUATION row (never the parent's), since two
  // different continuation rows disagreeing with the same parent are two separate questions. ---
  for (const problem of eff.secondSalesProblems || []) {
    const key = shopKey(sheet, problem.row);
    const { answered, value } = readDecision(secondSalesDecisions, key);
    const pushUnresolved = () =>
      unresolved.push({
        type: "possible-second-sales",
        sheet,
        decisionKey: key,
        row: problem.row,
        parentRow: problem.parentRow,
        name: eff.name,
        parentSales: problem.parentSales,
        rowSales: problem.rowSales,
        parentAddress: problem.parentAddress,
        rowAddress: problem.rowAddress,
        parentEkspedisi: problem.parentEkspedisi,
        rowEkspedisi: problem.rowEkspedisi,
        parentContactPerson: problem.parentContactPerson,
        rowContactPerson: problem.rowContactPerson,
        // Names the actual rows and the two Sales values, not just the general rule — a human
        // reviewing this needs to see THIS case, not a description of how the check works.
        message: `Baris ${problem.row} tidak punya Nama Toko, jadi ikut toko di baris ${problem.parentRow} ("${eff.name}"). Tapi Sales-nya beda — baris ${problem.parentRow} "${problem.parentSales}", baris ${problem.row} "${problem.rowSales}". Apakah toko ini punya dua sales?`,
      });
    if (!answered) {
      blocked = true;
      pushUnresolved();
      continue;
    }
    const { action: secondAction } = normalizeSecondSalesDecision(value);
    if (secondAction === "add") {
      salesNames.push(problem.rowSales);
    } else if (secondAction === "keep") {
      // Deliberately contributes nothing more — the parent's own Sales (already in salesNames,
      // above) is all this shop keeps. Never the app's own default: only ever reached by an
      // explicit "keep" decision, same presence-not-truthiness discipline as every other type
      // here (readDecision already refused to treat "unanswered" as this).
    } else {
      blocked = true;
      pushUnresolved();
    }
  }

  if (blocked) return;

  const finalSalesNames = dedupeArray([salesNames]);
  if (finalSalesNames.length === 0) {
    excluded.push({
      type: "no-person",
      sheet,
      row: eff.anchorRow,
      name: eff.name,
      sourceRows: eff.sourceRows,
      message: `Toko "${eff.name}" tidak punya sales/person sama sekali (semua dilewati) — tidak dibuat sebagai kontak.`,
    });
    return;
  }

  plan.push({
    list: sheet,
    name: eff.name,
    addresses: finalAddresses.map((text) => ({ text, isDefault: false })),
    city: finalCity,
    contactPerson: collapseList(eff.contactPerson),
    email: collapseList(eff.email),
    keterangan: collapseList(eff.keterangan),
    npwp: eff.npwp ? collapseList(eff.npwp) : "",
    ekspedisi: dedupeArray(eff.ekspedisi),
    salesNames: finalSalesNames,
    _meta: {
      sheet,
      anchorRow: eff.anchorRow,
      sourceRows: eff.sourceRows,
      mergedFromRows: eff.mergedFromRows,
      citySource,
    },
  });
}

/** Attaches every `possible-second-sales` problem belonging to `eff` — matched by the
 *  CONTINUATION row being present in `eff.sourceRows` (never `eff.anchorRow`/name), which stays
 *  correct regardless of duplicate-name merging: a merged shop's `sourceRows` is every member
 *  row's own sourceRows concatenated, so a problem attached to any one member's own continuation
 *  row is still found. Scoped to the same `sheet` so a same-numbered row on the OTHER sheet can
 *  never falsely match. Mutates and returns `eff` (called immediately after building it, before
 *  `eff` is exposed anywhere else) — not a separate pass over already-published effective shops. */
function withSecondSalesProblems(eff, allProblems) {
  const rowSet = new Set(eff.sourceRows);
  eff.secondSalesProblems = allProblems.filter(
    (p) => p.type === "possible-second-sales" && p.sheet === eff.sheet && rowSet.has(p.row)
  );
  return eff;
}

/**
 * Builds every EFFECTIVE shop from `parsed`'s own duplicate-name groups, resolved against ONLY
 * the duplicate-name decisions — the layer BELOW address/sales/city resolution
 * (`resolveEffectiveShop`, above). Exported (batch 5a addition) specifically so a caller that
 * needs a shop's STABLE identity before the other four ambiguities are answered — e.g. a wizard
 * step letting a human override an already-successfully-derived city — never has to re-derive
 * this grouping/merging logic a second time. `resolveContactImport` itself is built ON TOP of
 * this function (never a second, parallel copy of it), so the two can never drift apart.
 * Pure, deterministic, never mutates `parsed`.
 * @param {object} parsed — `parseContactWorkbook`'s own output.
 * @param {object} [duplicateNameDecisions] — same shape as `decisions.duplicateNames` in
 *   `resolveContactImport`; defaults to `{}` (nothing answered).
 * @returns {{effectiveShops: object[], unresolved: object[]}} Each effective shop also carries
 *   its own `secondSalesProblems` (possibly empty) — see `withSecondSalesProblems`, above; this
 *   is what lets a caller review "possible second salesperson" decisions before the other four
 *   ambiguities are answered, the same reason `buildEffectiveShops` exists at all. `unresolved`
 *   here still carries ONLY `duplicate-name`/`duplicate-name-conflict` entries, in the exact
 *   shape `resolveContactImport`'s own `unresolved` carries for those two types — a
 *   `possible-second-sales` decision is not resolved until `resolveContactImport` itself runs.
 *   A group with no decision yet (or an unresolved rename conflict) contributes NOTHING to
 *   `effectiveShops` — there is no stable identity to hand back until a human answers it.
 */
export function buildEffectiveShops(parsed, duplicateNameDecisions) {
  const duplicateDecisions = duplicateNameDecisions || {};
  const effectiveShops = [];
  const unresolved = [];
  const allProblems = (parsed && parsed.problems) || [];

  for (const sheet of ["CABANG", "PUSAT"]) {
    const sheetData = parsed && parsed.sheets && parsed.sheets[sheet];
    const shops = (sheetData && sheetData.shops) || [];
    if (shops.length === 0) continue;

    const groups = groupShopsByName(shops);
    for (const groupShops of groups.values()) {
      const sorted = [...groupShops].sort((a, b) => a.row - b.row);

      if (sorted.length === 1) {
        effectiveShops.push(withSecondSalesProblems(buildSingleEffective(sheet, sorted[0]), allProblems));
        continue;
      }

      const dGroupKey = duplicateGroupKey(sheet, sorted[0].name);
      const rows = sorted.map((s) => s.row);
      const pushDuplicateUnresolved = () =>
        unresolved.push({
          type: "duplicate-name",
          sheet,
          decisionKey: dGroupKey,
          name: sorted[0].name,
          rows,
          message: `Nama toko "${sorted[0].name}" muncul ${sorted.length} kali di sheet "${sheet}" (baris ${rows.join(", ")}) — perlu diputuskan: satu toko, atau ${sorted.length} toko terpisah.`,
        });

      const { answered, value } = readDecision(duplicateDecisions, dGroupKey);
      if (!answered) {
        pushDuplicateUnresolved();
        continue;
      }
      const { kind, names } = normalizeDuplicateDecision(value);

      if (kind === "merge") {
        effectiveShops.push(withSecondSalesProblems(buildMergedEffective(sheet, sorted), allProblems));
        continue;
      }

      if (kind === "separate") {
        const renamed = sorted.map((s) => ({
          shop: s,
          finalName: (names && typeof names[s.row] === "string" && names[s.row].trim()) || s.name,
        }));
        const conflictGroups = groupBy(renamed, (r) => normShopName(r.finalName));
        const hasConflict = [...conflictGroups.values()].some((g) => g.length > 1);
        if (hasConflict) {
          unresolved.push({
            type: "duplicate-name-conflict",
            sheet,
            decisionKey: dGroupKey,
            name: sorted[0].name,
            rows,
            message: `Toko "${sorted[0].name}" dipilih sebagai ${sorted.length} toko terpisah, tapi masih ada nama yang sama setelah diganti — setiap toko harus punya nama yang berbeda di sheet "${sheet}".`,
          });
          continue;
        }
        for (const r of renamed) {
          effectiveShops.push(withSecondSalesProblems(buildSingleEffective(sheet, r.shop, r.finalName), allProblems));
        }
        continue;
      }

      // Unrecognized/malformed kind — treat exactly like unanswered, never guess.
      pushDuplicateUnresolved();
    }
  }

  return { effectiveShops, unresolved };
}

/**
 * Resolves an already-parsed contacts workbook (`contactImportParser.js`'s `parseContactWorkbook`
 * output) against a set of user decisions. Pure, deterministic, never mutates either argument.
 * @param {{sheets: {CABANG: object|null, PUSAT: object|null}, problems: object[]}} parsed
 * @param {{duplicateNames?: object, extraAddresses?: object, unusableSales?: object,
 *   missingAddress?: object, city?: object, secondSales?: object}} decisions
 * @returns {{plan: object[], unresolved: object[], excluded: object[], readyToCommit: boolean}}
 */
export function resolveContactImport(parsed, decisions) {
  const dec = decisions || {};
  const ctx = {
    addressDecisions: dec.extraAddresses || {},
    salesDecisions: dec.unusableSales || {},
    missingAddressDecisions: dec.missingAddress || {},
    cityDecisions: dec.city || {},
    secondSalesDecisions: dec.secondSales || {},
    unresolved: [],
    excluded: [],
    plan: [],
  };

  const { effectiveShops, unresolved: duplicateUnresolved } = buildEffectiveShops(parsed, dec.duplicateNames);
  ctx.unresolved.push(...duplicateUnresolved);
  for (const eff of effectiveShops) {
    resolveEffectiveShop(eff.sheet, eff, ctx);
  }

  return {
    plan: ctx.plan,
    unresolved: ctx.unresolved,
    excluded: ctx.excluded,
    readyToCommit: ctx.unresolved.length === 0,
  };
}

/**
 * Read-only convenience for batch 5: what would still need asking, grouped by ambiguity type.
 * Never re-derives grouping logic of its own — delegates straight to `resolveContactImport`, so
 * the two can never drift apart.
 * @param {object} parsed — `parseContactWorkbook`'s own output.
 * @param {object} [decisions] — batch 5a addition: an optional, possibly PARTIAL decisions
 *   object (same five-sub-map shape `resolveContactImport` takes), so a wizard step can ask "what
 *   still needs deciding, GIVEN what's already been answered so far" — e.g. step 3 (extra
 *   addresses) only exists once step 2's duplicate-name decisions are applied, since merging
 *   changes which rows even have >1 address. Defaults to `{}` (nothing answered), which
 *   reproduces the original zero-argument behavior byte-for-byte — every existing caller is
 *   unaffected.
 */
export function collectDecisionsNeeded(parsed, decisions = {}) {
  const { unresolved } = resolveContactImport(parsed, decisions);
  const by = { duplicateNames: [], extraAddresses: [], unusableSales: [], missingAddress: [], cityNotDerived: [], secondSales: [] };
  for (const u of unresolved) {
    if (u.type === "duplicate-name" || u.type === "duplicate-name-conflict") by.duplicateNames.push(u);
    else if (u.type === "extra-address") by.extraAddresses.push(u);
    else if (u.type === "unusable-sales") by.unusableSales.push(u);
    else if (u.type === "missing-address") by.missingAddress.push(u);
    else if (u.type === "city-not-derived") by.cityNotDerived.push(u);
    else if (u.type === "possible-second-sales") by.secondSales.push(u);
  }
  return by;
}
